import type React from 'react'
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { type ThemeMode, createTheme } from '../theme/theme'

interface ThemeContextType {
  themeMode: ThemeMode
  actualTheme: 'light' | 'dark'
  setThemeMode: (mode: ThemeMode) => void
  theme: ReturnType<typeof createTheme>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: ReactNode
}

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'dark' // Default fallback
}

const getStoredTheme = (): ThemeMode => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('theme-mode')
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as ThemeMode
    }
  }
  return 'system' // Default to system preference
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getStoredTheme)
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(getSystemTheme)

  // Determine the actual theme to use
  const actualTheme = themeMode === 'system' ? systemTheme : themeMode

  // Memoize theme creation to prevent unnecessary re-renders
  const theme = useMemo(() => createTheme(actualTheme), [actualTheme])

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        setSystemTheme(e.matches ? 'dark' : 'light')
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  // Memoize setThemeMode to prevent recreation on every render
  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme-mode', mode)
    }
  }, [])

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      themeMode,
      actualTheme,
      setThemeMode,
      theme,
    }),
    [setThemeMode, themeMode, actualTheme, theme]
  )

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>
}
